/**
 * Parse & compare report note / policy references for View/Edit "Note sequence".
 * Supports: 6, 5.4, 5.4(a), 5.4 a, (a), a), a., a TITLE (bare letter policies).
 */

export type NoteSortPart =
  | { kind: 'n'; value: number }
  | { kind: 'a'; value: number };

/** Strip HTML to readable text (keeps spaces). */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Prefer the first bold / heading chunk (note titles are usually bold),
 * then fall back to leading plain text.
 */
export function extractNoteHeadingText(html: string): string {
  if (!html) return '';

  const boldChunk =
    html.match(/<(?:p|h[1-6])[^>]*>[\s\S]*?<\/(?:p|h[1-6])>/i)?.[0] ||
    html.match(/<(?:strong|b)[^>]*>[\s\S]*?<\/(?:strong|b)>/i)?.[0] ||
    '';

  const fromBold = htmlToPlainText(boldChunk);
  if (fromBold) return fromBold;

  return htmlToPlainText(html).slice(0, 120);
}

/** Max preview width in the text-block input (e.g. "4.2 FUNCTIONAL AND PRESENTATION CURR"). */
const TEXT_BLOCK_PREVIEW_MAX_LEN = 36;

function truncatePreviewText(text: string, maxLen: number): string {
  if (!text) return '';
  if (text.length <= maxLen) return text;

  // Keep final string (including "...") within the preview field width.
  const budget = Math.max(1, maxLen - 3);
  const words = text.split(' ');
  let truncated = '';
  for (const word of words) {
    const next = truncated ? `${truncated} ${word}` : word;
    if (next.length > budget) break;
    truncated = next;
  }
  if (!truncated) truncated = text.slice(0, budget);
  return `${truncated}...`;
}

function isTitleLikeParagraph(htmlChunk: string, plain: string): boolean {
  if (!plain) return false;
  // Short / numbered note titles, or explicitly bold heading paragraphs.
  if (/^\d+(?:\.\d+)*/.test(plain)) return true;
  if (plain.length <= 80) {
    if (/font-weight\s*:\s*(bold|[6-9]00)/i.test(htmlChunk)) return true;
    if (/<(?:strong|b)\b/i.test(htmlChunk)) return true;
    if (/<h[1-6]\b/i.test(htmlChunk)) return true;
  }
  return plain.length <= 48;
}

/**
 * Compact label for text-block preview inputs.
 * Prefers the first title-like paragraph (e.g. "5 EQUIPMENT").
 * Without a title: first non-empty body sentence outside tables (never table-header junk).
 * Always capped to the preview field width; &nbsp; decoded via htmlToPlainText.
 */
export function getTextBlockPreview(
  html: string,
  maxLen = TEXT_BLOCK_PREVIEW_MAX_LEN
): string {
  if (!html) return '';

  // Ignore table markup so previews don't become "2025Number..." / "&nbsp;".
  const withoutTables = html.replace(/<table\b[\s\S]*?<\/table>/gi, ' ');

  const blocks =
    withoutTables.match(/<(?:p|h[1-6]|div)[^>]*>[\s\S]*?<\/(?:p|h[1-6]|div)>/gi) || [];

  let title = '';
  let bodyFallback = '';

  for (const block of blocks) {
    const plain = htmlToPlainText(block);
    if (!plain) continue;

    if (!title && isTitleLikeParagraph(block, plain)) {
      title = plain;
      break;
    }
    if (!bodyFallback) bodyFallback = plain;
  }

  if (!title && !bodyFallback) {
    // No usable blocks outside tables — try remaining non-table text, then whole HTML.
    bodyFallback = htmlToPlainText(withoutTables) || htmlToPlainText(html);
  }

  return truncatePreviewText(title || bodyFallback, maxLen);
}

/**
 * Extract leading note/policy ref label from heading text.
 * Examples: "6", "5.4", "5.4(a)", "(a)", "a", "c Cash and Cash Equivalents"
 */
export function extractReportNoteLabel(htmlOrText: string): string | null {
  const text = htmlOrText.includes('<')
    ? extractNoteHeadingText(htmlOrText)
    : htmlOrText.replace(/\s+/g, ' ').trim();

  if (!text) return null;

  // 5.4(a) / 5.4(a) TITLE / 5.4 a TITLE / 13 OTHER INCOME
  const numericMixed = text.match(
    /^(\d+(?:\.\d+)*(?:\s*[\(\[]\s*[a-zA-Z]\s*[\)\]]|\s+[a-zA-Z]\b)?)/
  );
  if (numericMixed) {
    return normalizeNoteLabel(numericMixed[1]);
  }

  // (a) TITLE
  const parenAlpha = text.match(/^\(\s*([a-zA-Z])\s*\)/);
  if (parenAlpha) return `(${parenAlpha[1].toLowerCase()})`;

  // a) TITLE  or  a. TITLE
  const alphaSuffix = text.match(/^([a-zA-Z])\s*[\)\.]\s+/);
  if (alphaSuffix) return `(${alphaSuffix[1].toLowerCase()})`;

  // Bare policy letter: "c Cash and Cash Equivalents" / "a Basis of preparation".
  // Lowercase only so prose like "The Company…" / "A private…" is not treated as a ref.
  const bareAlpha = text.match(/^([a-z])\s+\S/);
  if (bareAlpha) return `(${bareAlpha[1]})`;

  return null;
}

function normalizeNoteLabel(raw: string): string {
  return raw
    .replace(/\s+/g, '')
    .replace(/[\[\]]/g, '')
    .replace(/\(([a-zA-Z])\)/i, (_, a) => `(${a.toLowerCase()})`)
    .replace(/([0-9])([a-zA-Z])$/i, (_, n, a) => `${n}(${a.toLowerCase()})`)
    .toLowerCase();
}

/** Convert a label like "5.4(a)" into comparable parts. */
export function parseReportNoteSortKey(label: string | null | undefined): NoteSortPart[] | null {
  if (!label) return null;
  const s = normalizeNoteLabel(label);
  if (!s) return null;

  const parts: NoteSortPart[] = [];

  // Pure alpha "(a)"
  const onlyAlpha = s.match(/^\(([a-z])\)$/);
  if (onlyAlpha) {
    parts.push({ kind: 'a', value: onlyAlpha[1].charCodeAt(0) - 97 });
    return parts;
  }

  // 5.4(a) or 5.4
  const main = s.match(/^(\d+(?:\.\d+)*)(?:\(([a-z])\))?$/);
  if (!main) return null;

  main[1].split('.').forEach((seg) => {
    const n = parseInt(seg, 10);
    if (!Number.isNaN(n)) parts.push({ kind: 'n', value: n });
  });

  if (main[2]) {
    parts.push({ kind: 'a', value: main[2].charCodeAt(0) - 97 });
  }

  return parts.length ? parts : null;
}

export function getReportNoteSortKeyFromHtml(html: string): NoteSortPart[] | null {
  return parseReportNoteSortKey(extractReportNoteLabel(html));
}

export function compareReportNoteSortKeys(
  a: NoteSortPart[] | null,
  b: NoteSortPart[] | null
): number {
  // Parsed notes first; unparsed keep relative order via caller tie-break
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const ap = a[i];
    const bp = b[i];
    if (!ap) return -1; // shorter prefix first: 5 before 5.4 before 5.4(a)
    if (!bp) return 1;
    if (ap.kind !== bp.kind) {
      return ap.kind === 'n' ? -1 : 1; // numbers before letters at same depth
    }
    if (ap.value !== bp.value) return ap.value - bp.value;
  }
  return 0;
}

export function isNotePolicyOrOtherSection(sectionCode: string): boolean {
  return (
    sectionCode.startsWith('720000_') || // Accounting policies
    sectionCode.startsWith('730000_') // Notes and other explanatory info
  );
}
